/**
 * NeedsReviewCard — eine Suggestion der „Braucht deinen Blick"-Liste:
 * Daten-Mapping `ChannelContentSuggestion` → `OptimierungSuggestionCard`-Props,
 * lokales Inline-Edit-Staging (Speichern ersetzt nur die Anzeige; „Übernehmen"
 * sendet den gestagten Wert als `edited_value`), Discard-Trigger (T7).
 */
import { useState } from 'react'

import { OptimierungSuggestionCard, type OptimierungSuggestionCardProps } from '@/components/v2/products/optimierungen'
import type { ChannelContentSuggestion } from '@/lib/api/channel-content-bulk'

import { SuggestionInlineEdit } from './SuggestionInlineEdit'

export interface NeedsReviewCardProps {
  suggestion: ChannelContentSuggestion
  channelName: string
  productLabel: React.ReactNode
  busy: boolean
  onApply: (editedValue?: string | string[]) => void
  onDiscard: () => void
}

export function NeedsReviewCard({
  suggestion,
  channelName,
  productLabel,
  busy,
  onApply,
  onDiscard,
}: NeedsReviewCardProps) {
  const [editing, setEditing] = useState(false)
  const [staged, setStaged] = useState<string | string[] | null>(null)
  const effectiveValue = staged ?? suggestion.suggestion

  return (
    <div className="text-bulk-review-card space-y-1">
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {productLabel} · {channelName}
      </p>
      <OptimierungSuggestionCard
        idSuffix={suggestion.id}
        channelName={channelName}
        busy={busy}
        reasoning={suggestion.reasoning}
        pushStatus={suggestion.push_status}
        field={{
          // A1-Typ (ContentFieldSuggestion) kennt seo_title/seo_description nicht (bewusst
          // nicht angetastet, s. Plan T1) — attribute_code wird hier nur angezeigt (attrLabel).
          attribute_code: suggestion.attribute_code as OptimierungSuggestionCardProps['field']['attribute_code'],
          previous: suggestion.previous_value,
          suggestion: effectiveValue,
          rule_compliant: suggestion.rule_compliant,
          triggered_rules: suggestion.triggered_rules,
        }}
        onApply={() => onApply(staged ?? undefined)}
        onDiscard={onDiscard}
        onEdit={() => setEditing(true)}
        editSlot={
          editing ? (
            <SuggestionInlineEdit
              idSuffix={suggestion.id}
              value={effectiveValue}
              onCancel={() => setEditing(false)}
              onSave={(value) => {
                setStaged(value)
                setEditing(false)
              }}
            />
          ) : undefined
        }
      />
    </div>
  )
}
